// ═══════════════════════════════════════════════════════════════════════════
// Student submissions (Arkiv, LOT3) — camelCase mirror of the FROZEN API contract
// backend/Parcours.Core/Modules/StudentSubmissionsRouteDto.cs (#629).
// Any change goes through an explicit review of the backend file:
// never let these types drift independently.
//
// The bytes NEVER go through these routes: uploading/downloading contents
// happens through the fondation proxy /api/core/proxy/arkiv/documents/{id}/versions[...]
// — Arkiv is referenced by document id ONLY (never by path).
// ═══════════════════════════════════════════════════════════════════════════

/** Keys from BuiltInSubmissionKindKeys (stored on StudentSubmission.Kind). */
export type SubmissionKind = 'AbsenceJustification' | 'ExamSubmission';

/** Canonical built-in set, with display labels (mirror of BuiltInSubmissionKindKeys.All). */
export const SUBMISSION_KINDS: { value: SubmissionKind; label: string }[] = [
  { value: 'AbsenceJustification', label: "Justification d'absence" },
  { value: 'ExamSubmission', label: "Remise d'examen" },
];

/** Technical life cycle of the submission (serialized as text by the backend). */
export type StudentSubmissionStatus = 'Pending' | 'Ready' | 'Failed' | 'DocumentDisposed';

/** Descriptor of a file to submit (metadata only — the bytes go through the Arkiv proxy). */
export interface SubmissionFileDescriptor {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

/** Body de POST /api/Frequentations/Mine/{fid}/AbsenceJustifications. */
export interface CreateAbsenceJustificationRequest {
  /** Start (inclusive) of the justified absence range — DateOnly « yyyy-MM-dd ». */
  absenceStartDate: string;
  /** End (inclusive) of the justified absence range — DateOnly « yyyy-MM-dd ». */
  absenceEndDate: string;
  studentComment?: string | null;
  files: SubmissionFileDescriptor[];
}

/** Body de POST /api/Frequentations/Mine/{fid}/Exams/{examId}/Submissions. */
export interface CreateExamSubmissionRequest {
  studentComment?: string | null;
  files: SubmissionFileDescriptor[];
}

/** Body de POST /api/Submissions/Mine/{id}/Files. */
export interface AddSubmissionFilesRequest {
  files: SubmissionFileDescriptor[];
}

/** A file created when starting a submission: the local id and the id of the target
 *  Arkiv document, to which the client then uploads the bytes through the proxy. */
export interface CreatedSubmissionFile {
  fileId: string;
  arkivDocumentId: string;
}

/** Response when starting a submission (justification or exam submission). */
export interface CreateSubmissionResponse {
  submissionId: string;
  files: CreatedSubmissionFile[];
}

/** Response of POST /api/Submissions/Mine/{id}/Files. */
export interface AddSubmissionFilesResponse {
  files: CreatedSubmissionFile[];
}

export interface StudentSubmissionFile {
  id: string;
  /** THE link to Arkiv — the content is read through
   *  /api/core/proxy/arkiv/documents/{arkivDocumentId}/versions[...]. */
  arkivDocumentId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export interface StudentSubmission {
  id: string;
  studentId: string;
  frequentationId: string;
  /** Key from SUBMISSION_KINDS: « AbsenceJustification » | « ExamSubmission ». */
  kind: string;
  status: StudentSubmissionStatus;
  studentComment?: string | null;
  /** School year frozen at submission time (e.g. « 2025-2026 »). */
  schoolYear: string;
  archived: boolean;
  /** Ancre d'une remise d'examen. */
  examId?: string | null;
  /** Anchor of a justification: the justified range (inclusive), « yyyy-MM-dd ». */
  absenceStartDate?: string | null;
  absenceEndDate?: string | null;
  createdDate?: string | null;
  files: StudentSubmissionFile[];
  /** The presences covered by this submission (justification only). */
  presenceIds: string[];
}

export interface StudentSubmissionResponse {
  submission: StudentSubmission;
}

export interface StudentSubmissionsResponse {
  submissions: StudentSubmission[];
}

/** Response of GET /api/Presences/Submissions?presenceIds= — the submissions covering
 *  each requested presence, indexed by presence id (missing keys = no submission). */
export interface PresenceSubmissionsResponse {
  submissionsByPresence: Record<string, StudentSubmission[]>;
}
